import {
    CARGAR_ESTADISTICAS_PACIENTE,
    CARGAR_ESTADISTICAS_PACIENTE_EXITO,
    CARGAR_ESTADISTICAS_PACIENTE_ERROR
} from '../types';

import clienteAxios from '../config/axios';


/*
====================================
CARGAR ESTADÍSTICAS DEL PACIENTE
====================================

Carga en una sola llamada:

- asistencia;
- presentismo;
- ausentismo;
- resumen económico;
- cargos;
- detalle de turnos.

El período puede ser:

- mes;
- año;
- personalizado.

El frontend siempre traduce esos filtros
a fecha_desde + fecha_hasta.

====================================
*/

export function cargarEstadisticasPacienteAction({
    config,
    id_paciente,
    fecha_desde = null,
    fecha_hasta = null
}) {

    return async (dispatch) => {

        dispatch(
            cargarEstadisticasPaciente()
        );

        try {

            /*
            ====================================
            ARMAR PAYLOAD
            ====================================
            */

            const payload = {
                id_paciente
            };


            if (fecha_desde) {
                payload.fecha_desde =
                    fecha_desde;
            }


            if (fecha_hasta) {
                payload.fecha_hasta =
                    fecha_hasta;
            }


            /*
            ====================================
            CONSULTAR BACKEND
            ====================================
            */

            const respuesta =
                await clienteAxios.post(
                    '/paciente/estadisticaspaciente',
                    payload,
                    config
                );


            /*
            ====================================
            GUARDAR EN REDUX
            ====================================
            */

            dispatch(
                cargarEstadisticasPacienteExito(
                    respuesta.data
                )
            );


            /*
            ====================================
            DEVOLVER RESPUESTA
            ====================================

            Esto permite que el componente,
            si alguna vez lo necesita, pueda hacer:

            const resultado = await dispatch(...)

            sin obligarnos a consultar Redux
            inmediatamente después.
            ====================================
            */

            return respuesta.data;

        } catch (error) {

            const mensajeError =
                error.response?.data ||
                {
                    msg:
                        'No se pudieron cargar las estadísticas del paciente.'
                };


            dispatch(
                cargarEstadisticasPacienteError(
                    mensajeError
                )
            );


            /*
            ====================================
            PROPAGAR ERROR
            ====================================

            Igual que en Calendario, dejamos
            que el componente pueda decidir
            si muestra SweetAlert u otro mensaje.
            ====================================
            */

            throw error;

        }

    };

}


/*
====================================
SOLICITANDO INFORMACIÓN
====================================
*/

const cargarEstadisticasPaciente = () => ({
    type: CARGAR_ESTADISTICAS_PACIENTE,
    payload: true
});


/*
====================================
ESTADÍSTICAS CARGADAS
====================================
*/

const cargarEstadisticasPacienteExito = (data) => ({
    type: CARGAR_ESTADISTICAS_PACIENTE_EXITO,
    payload: data
});


/*
====================================
ERROR AL CARGAR
====================================
*/

const cargarEstadisticasPacienteError = (error) => ({
    type: CARGAR_ESTADISTICAS_PACIENTE_ERROR,
    payload: error
});

/*
====================================
CARGAR HISTORIAL DE COBROS
====================================

El historial se consulta solamente
cuando el profesional lo solicita.

No se guarda en Redux porque no forma
parte de las estadísticas principales
y no necesitamos mantenerlo cargado.

Incluye pagos activos y anulados para
poder reconstruir correctamente el
historial de la cuenta.
====================================
*/

export function cargarHistorialPagosCuentaAction({
    config,
    cuenta_id
}) {

    return async () => {

        try {

            const respuesta =
                await clienteAxios.post(
                    '/paciente/historialpagoscuenta',
                    {
                        cuenta_id,
                        incluir_anulados: true
                    },
                    config
                );


            return respuesta.data;

        } catch (error) {

            throw error;

        }

    };

}

/*
====================================
REGISTRAR COBRO
====================================

Registra un pago para los cargos
seleccionados.

El frontend NO decide cuánto se imputa
a cada cargo.

El backend distribuye automáticamente
el importe entre los cargos enviados,
comenzando por los más antiguos.
====================================
*/

export function registrarPagoAction({
    config,
    fecha_pago,
    importe,
    medio_pago,
    observacion,
    cargos_ids
}) {

    return async () => {

        try {

            const respuesta =
                await clienteAxios.post(
                    '/paciente/registrarpago',
                    {
                        fecha_pago,
                        importe,
                        medio_pago,
                        observacion,
                        cargos_ids
                    },
                    config
                );


            return respuesta.data;

        } catch (error) {

            throw error;

        }

    };

}

/*
====================================
CAMBIAR COBRABLE DEL CARGO
====================================

Permite indicar si un cargo debe
generar deuda o no.

El cargo no se elimina:
conserva su importe histórico.

Si el cargo ya tiene cobros activos,
el backend puede bloquear el cambio
con CARGO_CON_COBROS.
====================================
*/

export function cambiarCobrableCargoAction({
    config,
    cargo_id,
    cobrable
}) {

    return async () => {

        try {

            const respuesta =
                await clienteAxios.post(
                    '/paciente/cambiarcobrablecargo',
                    {
                        cargo_id,
                        cobrable
                    },
                    config
                );


            return respuesta.data;

        } catch (error) {

            throw error;

        }

    };

}

/*
====================================
ANULAR PAGO
====================================

Un pago no se modifica parcialmente.

Para corregir un cobro se anula el
pago completo y luego, si corresponde,
se registra uno nuevo.

El backend también anula todas las
imputaciones activas asociadas a ese
pago.
====================================
*/

export function anularPagoAction({
    config,
    pago_id
}) {

    return async () => {

        try {

            const respuesta =
                await clienteAxios.post(
                    '/paciente/anularpago',
                    {
                        pago_id
                    },
                    config
                );


            return respuesta.data;

        } catch (error) {

            throw error;

        }

    };

}

/*
====================================
CARGAR ECONOMÍA COMPLETA DEL PACIENTE
====================================

Se utiliza al abrir "Registrar cobro".

No enviamos fecha_desde ni fecha_hasta
porque necesitamos conocer todas las
deudas activas del paciente, incluyendo
las anteriores al período que se está
visualizando.

Esta consulta se realiza únicamente
cuando el usuario abre el modal.
====================================
*/

export function cargarResumenEconomicoCompletoAction({
    config,
    id_paciente
}) {

    return async () => {

        try {

            const respuesta =
                await clienteAxios.post(
                    '/paciente/resumeneconomicopaciente',
                    {
                        id_paciente
                    },
                    config
                );


            return respuesta.data;

        } catch (error) {

            throw error;

        }

    };

}

/*
====================================
APLICAR SALDO A FAVOR
====================================

Utiliza el saldo a favor existente
en la cuenta para cancelar deudas
pendientes.

El frontend solamente envía cuenta_id.

El backend:

- valida que la cuenta pertenezca
  al profesional autenticado;
- toma saldo de pagos REGISTRADOS;
- utiliza imputaciones ACTIVAS;
- aplica primero los saldos más antiguos;
- cancela primero las deudas más antiguas;
- no crea un pago nuevo;
- no permite elegir importes manualmente.
====================================
*/

export function aplicarSaldoAFavorAction({
    config,
    cuenta_id
}) {

    return async () => {

        try {

            const respuesta =
                await clienteAxios.post(
                    '/paciente/aplicarsaldoafavor',
                    {
                        cuenta_id
                    },
                    config
                );


            return respuesta.data;

        } catch (error) {

            throw error;

        }

    };

}

/*
====================================
CARGAR ESTADÍSTICAS DEL PROFESIONAL
====================================

Carga en una sola llamada las
estadísticas de todos los pacientes
del profesional.

Utiliza los mismos estados de Redux
que la vista individual porque la
estructura de respuesta es compatible.
====================================
*/

export function cargarEstadisticasProfesionalAction({
    config,
    fecha_desde = null,
    fecha_hasta = null
}) {

    return async (dispatch) => {

        dispatch({
            type:
                CARGAR_ESTADISTICAS_PACIENTE
        });


        try {

            const respuesta =
                await clienteAxios.post(
                    '/paciente/estadisticasprofesional',
                    {
                        fecha_desde,
                        fecha_hasta
                    },
                    config
                );


            dispatch({
                type:
                    CARGAR_ESTADISTICAS_PACIENTE_EXITO,

                payload:
                    respuesta.data
            });


            return respuesta.data;

        } catch (error) {

            dispatch({
                type:
                    CARGAR_ESTADISTICAS_PACIENTE_ERROR,

                payload:
                    error.response?.data?.msg ||
                    error.response?.data?.message ||
                    'No se pudieron cargar las estadísticas generales.'
            });


            throw error;

        }

    };

}